import { useRef, useState } from 'react';
import { Globe, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import GalleryQuickMenu from './GalleryQuickMenu';
import ShareModal from './ShareModal';
import type { GalleryLinkGate } from './shareGate';
import { usePublicLink } from './usePublicLink';

interface ShareControlProps {
  photosetId: string;
  /** The property's account, named by Internal Share. */
  accountName: string | null;
  /** The photoset's share token, null while the link is off. */
  token: string | null;
  /** Adopt a minted token, or null after a revoke. */
  onTokenChange: (token: string | null) => void;
  galleryLink: GalleryLinkGate;
  /** The Share modal's open state, held by the page so its page-level drop
   *  target knows a dialog is up. */
  modalOpen: boolean;
  onModalOpenChange: (open: boolean) => void;
}

/**
 * The Manage Assets header's split Share button (DV-3714, Header Actions 2b):
 * Share opens the Share modal; the globe opens the PlanOtour quick menu,
 * and carries a dot while the link is on. Both share one public-link state,
 * so the menu and the modal's tab are the same switch.
 */
const ShareControl = ({
  photosetId,
  accountName,
  token,
  onTokenChange,
  galleryLink,
  modalOpen,
  onModalOpenChange
}: ShareControlProps) => {
  const link = usePublicLink({ photosetId, token, onTokenChange });
  const [menuOpen, setMenuOpen] = useState(false);
  const linkOn = link.url !== null;
  // Opened from the quick menu, the modal's opener is a menu row that is gone
  // by the time it closes, so TabbedModal has nothing to return focus to.
  // Send it back to the globe the user started from instead.
  const globeRef = useRef<HTMLButtonElement>(null);
  const openedFromMenu = useRef(false);

  return (
    <>
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <div className="flex shrink-0 items-center">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              openedFromMenu.current = false;
              onModalOpenChange(true);
            }}
            aria-label="Share"
            title="Share Photoset"
            className="shrink-0 rounded-r-none"
          >
            <Share2 className="h-4 w-4" />
            <span className="max-lg:hidden">Share</span>
          </Button>
          <PopoverTrigger asChild>
            <Button
              ref={globeRef}
              type="button"
              variant="outline"
              size="icon"
              aria-label={linkOn ? 'PlanOtour link, on' : 'PlanOtour link'}
              title="PlanOtour link"
              className="relative w-9 shrink-0 rounded-l-none border-l-0 data-[state=open]:bg-accent max-lg:w-11"
            >
              <Globe className="h-4 w-4" />
              {linkOn && (
                <span
                  aria-hidden
                  data-testid="gallery-link-on-dot"
                  className="absolute right-[7px] top-2 h-2 w-2 rounded-full border-2 border-background bg-success"
                />
              )}
            </Button>
          </PopoverTrigger>
        </div>
        <PopoverContent
          align="end"
          aria-label="PlanOtour"
          className="w-[340px] max-w-[calc(100vw-32px)] p-1"
        >
          <GalleryQuickMenu
            link={link}
            gate={galleryLink}
            onAllOptions={() => {
              openedFromMenu.current = true;
              setMenuOpen(false);
              onModalOpenChange(true);
            }}
          />
        </PopoverContent>
      </Popover>
      {modalOpen && (
        <ShareModal
          open={modalOpen}
          onOpenChange={onModalOpenChange}
          photosetId={photosetId}
          accountName={accountName}
          link={link}
          galleryLink={galleryLink}
          onCloseAutoFocus={(event) => {
            if (!openedFromMenu.current) return;
            event.preventDefault();
            globeRef.current?.focus();
          }}
        />
      )}
    </>
  );
};

export default ShareControl;
